import { Paper } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { getLastPosition } from '../../../chess/gameTree'
import { gameFromPGN, getDataFromPGN, PGNFields } from '../../../chess/pgn'
import { Color } from '../../../chess/types'
import { ENV, OB_WS_URL } from '../../../env'
import ReconnectingWebSocket, {
    ErrorEvent,
    Options,
    WS_ERROR_EVENT_STATUS,
} from '../../../socket/ReconnectingWebsocket'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import store from '../../../store/store'
import { ConnectionStatus, GameResult, GameResultType } from '../../../store/types'
import { GAME_END_REASONS } from '../../gameView/components/gameServerConnector/GSTypes'

function ObserverServerConnector() {
    const connectionStatus = useStoreState((state) => state.observeView.connectionStatus)
    const token = useStoreState((state) => state.token)
    const serverConnectionData = useStoreState((state) => state.serverConnectionData)
    const setConnectionStatus = useStoreActions((state) => state.observeView.setConnectionStatus)
    const setSocket = useStoreActions((state) => state.observeView.setSocket)
    const initialiseGame = useStoreActions((state) => state.observeView.initialiseGame)
    const receiveMove = useStoreActions((state) => state.observeView.receiveMove)
    const setGameResult = useStoreActions((state) => state.observeView.setGameResult)
    const setGameTree = useStoreActions((state) => state.observeView.setGameTree)
    const setCurrentPositionId = useStoreActions((state) => state.observeView.setCurrentPositionId)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)
    const resetObserverState = useStoreActions((state) => state.observeView.resetObserverState)
    const resubscribe = useStoreActions((state) => state.observeView.resubscribe)
    const setOpening = useStoreActions((state) => state.observeView.setOpening)

    useEffect(() => {
        if (!token) return

        // A URL-provider function, not a frozen string — so a reconnect the class
        // triggers internally (dropped connection, appResume) re-reads the live
        // token instead of replaying whatever token this socket was built with.
        const url = () => `${OB_WS_URL}/v1/ws/observe?token=${store.getState().token}`
        const options: Options = {
            debug: true,
            debugName: 'OB',
            // Conservative backoff for observer (less critical)
            minReconnectionDelay: 2000,
            maxReconnectionDelay: 120000,
            reconnectionDelayGrowFactor: 2.5,
            // 20 retries
            maxRetries: 20,
            // TODO: Consider adding explicit keepalive config after coordinating with backend team
            // keepaliveDelay: 15000,
            // keepaliveTimeout: 5000,
            useJitter: true,
        }
        let ws = new ReconnectingWebSocket(url, undefined, options)

        // Track if this was an intentional close (user navigated away)
        let intentionalClose = false

        // Store original close method to intercept intentional closes
        const originalClose = ws.close.bind(ws)
        ws.close = (...args) => {
            intentionalClose = true
            return originalClose(...args)
        }

        ws.addEventListener('close', () => {
            console.log('observer ws closed, intentional:', intentionalClose)
            // Only set to CONNECTING if this wasn't an intentional disconnect
            if (!intentionalClose) {
                console.log('observer ws closed unexpectedly, attempting to reconnect')
                setConnectionStatus(ConnectionStatus.CONNECTING)
            } else {
                console.log('observer ws closed intentionally (user navigated away), not reconnecting')
            }
        })

        ws.addEventListener('open', () => {
            setConnectionStatus(ConnectionStatus.CONNECTED)
            setSocket(ws)
        })

        ws.addEventListener('reconnect', () => {
            console.log('observer ws reconnected after it broke')
            setConnectionStatus(ConnectionStatus.CONNECTED)
            setSocket(ws)
            resubscribe()
        })

        ws.addEventListener('error', (ev: ErrorEvent) => {
            // TIMEOUT / empty message are transient states the reconnect logic
            // below already handles — breadcrumb, not error. Only the terminal
            // MAX_RETRIES is worth reporting.
            Sentry.addBreadcrumb({
                category: 'ws',
                level: 'info',
                message: `observer error: ${ev.message || 'no message'}`,
            })

            switch (ev.message) {
                case WS_ERROR_EVENT_STATUS.TIMEOUT:
                    setConnectionStatus(ConnectionStatus.CONNECTING)
                    break
                case WS_ERROR_EVENT_STATUS.MAX_RETRIES:
                    Sentry.captureMessage('Observer connection failed after max retries', 'warning')
                    setConnectionStatus(ConnectionStatus.RECONNECT_FAILED)
                    break
                default:
                    // setConnectionStatus(ConnectionStatus.CONNECTION_ERROR)
                    // resetObserverState()
                    break
            }
        })

        ws.addEventListener('message', (me) => {
            let mData
            try {
                mData = JSON.parse(me.data)
            } catch {
                Sentry.addBreadcrumb({ category: 'ws', level: 'warning', message: 'observer: non-JSON frame' })
                return
            }

            if (mData.requestType === 'subscribeToMatch' && mData.result.status === 'error') {
                setSnackbarMessage('This game has already ended')
            }

            if (mData.requestType === 'subscribeToMatch' && mData.data) {
                // no clockFace before the first move; the previous game's tree must still be replaced
                if (!mData.data.clockFace) console.error('no clockFace in subscribeToMatch')
                const { pgn, clockFace } = mData.data
                const pgnData = getDataFromPGN(pgn, [PGNFields.Opening, PGNFields.GameTree])
                setOpening(pgnData.Opening || '')
                initialiseGame({ gameTree: pgnData.GameTree || gameFromPGN(''), clockFace })
            }

            if (mData.messageType === 'moveInfoWithClockFaceMessage' && mData.data) {
                if (!mData.data.lastMove) console.error('no lastMove in moveInfoWithClockFaceMessage')
                if (!mData.data.clockFace) console.error('no clockFace in moveInfoWithClockFaceMessage')
                if (mData.data.lastMove && mData.data.clockFace)
                    receiveMove({ ...mData.data, matchId: mData.matchId ?? mData.data.matchId })
                if (mData.data.opening) setOpening(mData.data.opening)
            }
            if (mData.messageType === 'endOfGameMessage') {
                const d = mData.data
                const result: GameResult = {
                    winner: undefined,
                    type: GameResultType.Unknown,
                    newRating: undefined,
                    clock: d.clock,
                    outcome: d.outcome,
                }
                if (d.pgn) {
                    const pgnData = getDataFromPGN(d.pgn, [PGNFields.Opening, PGNFields.GameTree])
                    const newGameTree = pgnData.GameTree || gameFromPGN('')
                    setOpening(pgnData.Opening || '')
                    setGameTree(newGameTree)
                    const lastPos = getLastPosition(newGameTree)
                    setCurrentPositionId(lastPos.id)
                }
                if (d.ratings) result.newRating = d.ratings

                // check the result who won ("0-1" is a fixed notation in chess!)
                if (d.outcome === '1-0') result.winner = Color.White
                else if (d.outcome === '0-1') result.winner = Color.Black

                // decipher the reason
                switch (d.reason) {
                    case GAME_END_REASONS.CHECK_MATE:
                        result.type = GameResultType.Checkmated
                        break
                    case GAME_END_REASONS.RESIGNATION:
                        result.type = GameResultType.Resigned
                        break
                    case GAME_END_REASONS.INSUFFICIENT_MATERIAL:
                        result.type = GameResultType.InsufficientMaterial
                        break
                    case GAME_END_REASONS.STALE_MATE:
                        result.type = GameResultType.Stalemate
                        break
                    case GAME_END_REASONS.THREE_FOLD_REPETITION:
                        result.type = GameResultType.ThreefoldRepetition
                        break
                    case GAME_END_REASONS.FIVE_FOLD_REPETITION:
                        result.type = GameResultType.FivefoldRepetition
                        break
                    case GAME_END_REASONS.FIFTY_MOVE_RULE:
                        result.type = GameResultType.FiftyMoveRule
                        break
                    case GAME_END_REASONS.SEVENTY_FIVE_MOVE_RULE:
                        result.type = GameResultType.SeventyFiveMoveRule
                        break
                    case GAME_END_REASONS.DRAW:
                        result.type = GameResultType.Draw
                        break
                    case GAME_END_REASONS.ABORTED:
                        result.type = GameResultType.Aborted
                        break
                    case GAME_END_REASONS.SECOND_PLAYER_JOIN_TIMEOUT:
                        result.type = GameResultType.SecondPlayerTimeout
                        break
                    case GAME_END_REASONS.CLOCK_EXPIRATION:
                        result.type = GameResultType.ForfeitedOnTime
                        break
                    case GAME_END_REASONS.TECHNICAL_ERROR_AI:
                        result.type = GameResultType.TechnicalErrorAi
                        break
                    case GAME_END_REASONS.NO_PLAYER1_MOVE_SINCE_START:
                    case GAME_END_REASONS.NO_PLAYER2_MOVE_SINCE_START:
                        result.type = GameResultType.NoPlayerMoveSinceStart
                        break
                    case GAME_END_REASONS.ALL_PLAYERS_DISCONNECTED:
                        result.type = GameResultType.AllPlayersDisconnected
                        break
                    case GAME_END_REASONS.PLAYER1_DISCONNECTED:
                    case GAME_END_REASONS.PLAYER2_DISCONNECTED:
                        result.type = GameResultType.PlayerDisconnected
                        break
                    case GAME_END_REASONS.REASON_NORMAL:
                    default:
                        result.type = GameResultType.Unknown
                        break
                }

                if (d.ratings) result.newRating = d.ratings

                setGameResult(result)
            }
        })

        return () => {
            console.log('resetting observer state in cleanup')
            ws.close()
            resetObserverState()
        }
        // Only depend on token - don't depend on store actions as they are stable references
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    return (
        <Paper
            className={'serverConnection gs'}
            style={{
                display: serverConnectionData ? 'block' : 'none',
            }}
        >
            OB: {connectionStatus} {OB_WS_URL} {ENV}{' '}
        </Paper>
    )
}

export default ObserverServerConnector
